/**
 * Workspace settings page for /workspace/settings route.
 * Allows workspace owners/admins to customize settings such as the output language.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { PageHeader } from "../components/layout/PageHeader";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { useProfile } from "../contexts/ProfileContext";
import { useIsWorkspaceAdmin } from "../hooks/useWorkspaceMembers";
import { useWorkspaceUrl, publicRoutes } from "../lib/routes";
import { useTemplateShapeRecipes, useWorkspaceProjectTemplates } from "../hooks/useProjectTemplates";
import { WorkspaceWorkflowCard } from "../components/admin/WorkspaceWorkflowCard";
import { updateWorkspaceSettings } from "../services/naraDoor";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  FolderPlus,
  Globe,
  Loader2,
  Tag as TagIcon,
  Building2,
} from "lucide-react";

type AutoProjectFlag = "auto_project_per_recording" | "auto_project_document";
type AutoProjectPick = "auto_project_template_id" | "auto_project_recipe_id";
type AutoProjectSettings = Record<AutoProjectFlag, boolean> & Record<AutoProjectPick, string | null>;

export function WorkspaceSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeWorkspace, isLoading: isWorkspaceLoading } = useWorkspace();
  const { isAdmin } = useProfile();
  // The workspace seat (owner/admin), not the platform `isAdmin` above.
  const isWorkspaceAdmin = useIsWorkspaceAdmin();
  const workspaceUrls = useWorkspaceUrl();

  // Output language (workspace-global, stored top-level in workspace.settings;
  // drives AI-generated content language such as transcription titles). Unset =>
  // backend defaults to French.
  const [language, setLanguage] = useState("");

  // Initialize the language control from the workspace's top-level settings.
  useEffect(() => {
    const current = (activeWorkspace?.settings as { language?: string } | undefined)?.language;
    if (current) setLanguage(current);
  }, [activeWorkspace?.settings]);

  // Language save — interfaceId "" writes a top-level key in workspace.settings
  // (not interface-scoped), which is where the transcription backend reads it.
  const updateLanguageMutation = useMutation({
    mutationFn: (lang: string) => {
      if (!activeWorkspace?.id) {
        throw new Error("No workspace selected");
      }
      return updateWorkspaceSettings(activeWorkspace.id, "", { language: lang });
    },
  });

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
    updateLanguageMutation.mutate(lang);
  };

  // T-366: per-workspace options the backend reads (missing = off / none); the Hub only stores them.
  // A choice shows at once and is dropped from the overrides if its save fails.
  const settings = activeWorkspace?.settings as Partial<AutoProjectSettings> | undefined;
  const [overrides, setOverrides] = useState<Partial<AutoProjectSettings>>({});
  const flagValue = (flag: AutoProjectFlag) => overrides[flag] ?? settings?.[flag] === true;
  const pickValue = (pick: AutoProjectPick) => (pick in overrides ? overrides[pick] : settings?.[pick]) || "";

  const perRecording = flagValue("auto_project_per_recording");
  const templateId = pickValue("auto_project_template_id");
  const showRecipes = perRecording && flagValue("auto_project_document") && !!templateId;
  const templatesQuery = useWorkspaceProjectTemplates(isWorkspaceAdmin && perRecording ? activeWorkspace?.id : undefined);
  const recipesQuery = useTemplateShapeRecipes(isWorkspaceAdmin && showRecipes ? activeWorkspace?.id : undefined, templateId);
  const templateOptions = templatesQuery.data?.templates ?? [];
  const recipeOptions = recipesQuery.data?.recipes ?? [];
  const loadFailed = templatesQuery.isError || recipesQuery.isError;

  useEffect(() => {
    if (templatesQuery.error) console.error("Failed to load project templates:", templatesQuery.error);
  }, [templatesQuery.error]);
  useEffect(() => {
    if (recipesQuery.error) console.error("Failed to load template recipes:", recipesQuery.error);
  }, [recipesQuery.error]);

  const updateAutoProjectMutation = useMutation({
    mutationFn: (patch: Partial<AutoProjectSettings>) => {
      if (!activeWorkspace?.id) {
        throw new Error("No workspace selected");
      }
      return updateWorkspaceSettings(activeWorkspace.id, "", patch);
    },
    onError: (error, patch) => {
      console.error("Failed to save auto-project setting:", Object.keys(patch), error);
      setOverrides((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(patch) as (keyof AutoProjectSettings)[]) delete next[key];
        return next;
      });
    },
  });

  const handleFlagChange = (flag: AutoProjectFlag, value: boolean) => {
    setOverrides((prev) => ({ ...prev, [flag]: value }));
    updateAutoProjectMutation.mutate({ [flag]: value });
  };

  // An empty choice sends null (the server removes the key). A new template drops the stored recipe server-side.
  const handlePickChange = (pick: AutoProjectPick, value: string) => {
    setOverrides((prev) => ({ ...prev, [pick]: value || null, ...(pick === "auto_project_template_id" && { auto_project_recipe_id: null }) }));
    updateAutoProjectMutation.mutate({ [pick]: value || null });
  };

  // Loading state
  if (isWorkspaceLoading) {
    return (
      <div>
        <PageHeader
          title={t("common.loading")}
          breadcrumbs={[
            { label: t("nav.dashboard"), href: publicRoutes.home },
            { label: t("workspaceSettings.title") },
          ]}
        />
        <div className="space-y-4">
          <div className="h-12 bg-muted rounded-lg animate-pulse" />
          <div className="h-64 bg-muted rounded-lg animate-pulse" />
        </div>
      </div>
    );
  }

  // No workspace selected
  if (!activeWorkspace) {
    return (
      <div>
        <PageHeader
          title={t("workspaceSettings.title")}
          breadcrumbs={[
            { label: t("nav.dashboard"), href: publicRoutes.home },
            { label: t("workspaceSettings.title") },
          ]}
        />
        <div className="flex items-center gap-3 p-4 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-800">
              {t("workspaceSettings.noWorkspace")}
            </p>
            <p className="text-xs text-yellow-600">
              {t("workspaceSettings.noWorkspaceMessage")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Get dashboard URL based on workspace handle
  const dashboardUrl = activeWorkspace.handle
    ? `/ws/${activeWorkspace.handle}/dashboard`
    : publicRoutes.home;

  const renderHeading = (i18nKey: string) => (
    <div className="flex items-center gap-3">
      <div className="p-2 bg-blue-50 dark:bg-blue-950 rounded-lg">
        <FolderPlus className="h-5 w-5 text-blue-600" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-foreground">{t(`workspaceSettings.autoProject.${i18nKey}.title`)}</h2>
        <p className="text-sm text-muted-foreground">{t(`workspaceSettings.autoProject.${i18nKey}.description`)}</p>
      </div>
    </div>
  );

  const renderToggle = (flag: AutoProjectFlag, i18nKey: string) => (
    <div key={flag} className="flex items-center justify-between gap-4">
      {renderHeading(i18nKey)}
      <label className="relative inline-flex items-center cursor-pointer shrink-0">
        <input
          type="checkbox"
          checked={flagValue(flag)}
          onChange={(e) => handleFlagChange(flag, e.target.checked)}
          disabled={updateAutoProjectMutation.isPending}
          aria-label={t(`workspaceSettings.autoProject.${i18nKey}.title`)}
          className="sr-only peer"
        />
        <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border dark:after:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50" />
      </label>
    </div>
  );

  const renderSelect = (pick: AutoProjectPick, i18nKey: string, options: { id: string; name: string }[], loading: boolean) => (
    <div key={pick} className="flex items-center justify-between gap-4">
      {renderHeading(i18nKey)}
      <div className="flex items-center gap-2 shrink-0">
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        <select
          value={pickValue(pick)}
          onChange={(e) => handlePickChange(pick, e.target.value)}
          disabled={updateAutoProjectMutation.isPending}
          aria-label={t(`workspaceSettings.autoProject.${i18nKey}.title`)}
          className="px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <option value="">{t(`workspaceSettings.autoProject.${i18nKey}.none`)}</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader
        title={t("workspaceSettings.title")}
        description={activeWorkspace.name}
        breadcrumbs={[
          { label: t("nav.dashboard"), href: dashboardUrl },
          { label: t("nav.settings") },
        ]}
        actions={
          <button
            onClick={() => navigate(dashboardUrl)}
            className="inline-flex items-center gap-2 px-3 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("common.back")}
          </button>
        }
      />

      {/* Tags Management */}
      {(() => {
        const tagAccess = (activeWorkspace?.settings?.tags as any)?.access || "custom";
        if (tagAccess === "none") return null;
        const tagDescription = tagAccess === "system"
          ? "View admin-configured tags for cross-project sharing"
          : "Create and manage tags to organize projects and enable cross-project search";
        return (
          <div
            onClick={() => navigate(publicRoutes.workspaceTags)}
            className="bg-card rounded-lg border border-border p-6 cursor-pointer hover:border-blue-300 dark:hover:border-blue-700 hover:bg-accent/50 transition-colors mb-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-50 dark:bg-purple-950 rounded-lg">
                  <TagIcon className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Tags</h2>
                  <p className="text-sm text-muted-foreground">
                    {tagDescription}
                  </p>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </div>
        );
      })()}

      {/* Business profiles (workspace owners/admins) */}
      {isWorkspaceAdmin && workspaceUrls && (
        <div
          onClick={() => navigate(workspaceUrls.businessProfiles)}
          className="bg-card rounded-lg border border-border p-6 cursor-pointer hover:border-blue-300 dark:hover:border-blue-700 hover:bg-accent/50 transition-colors mb-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 dark:bg-blue-950 rounded-lg">
                <Building2 className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">{t("businessProfiles.title")}</h2>
                <p className="text-sm text-muted-foreground">{t("businessProfiles.settingsHelp")}</p>
              </div>
            </div>
            <ArrowRight className="h-5 w-5 text-muted-foreground" />
          </div>
        </div>
      )}

      {/* Output Language (workspace-global) */}
      <div className="bg-card rounded-lg border border-border p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-emerald-50 dark:bg-emerald-950 rounded-lg">
            <Globe className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {t("workspaceSettings.language.title")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t("workspaceSettings.language.description")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={language || "fr"}
            onChange={(e) => handleLanguageChange(e.target.value)}
            disabled={updateLanguageMutation.isPending}
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <option value="fr">{t("workspaceSettings.language.french")}</option>
            <option value="en">{t("workspaceSettings.language.english")}</option>
          </select>
          {updateLanguageMutation.isPending && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          )}
          {updateLanguageMutation.isSuccess && !updateLanguageMutation.isPending && (
            <span className="text-sm text-green-600">{t("workspaceSettings.saveSuccess")}</span>
          )}
          {updateLanguageMutation.isError && (
            <span className="text-sm text-red-600">{t("workspaceSettings.saveError")}</span>
          )}
        </div>
      </div>

      {/* Auto-project options (workspace owners/admins; the server enforces it too) */}
      {isWorkspaceAdmin && (
        <div className="bg-card rounded-lg border border-border p-6 mb-6 space-y-5">
          {renderToggle("auto_project_per_recording", "perRecording")}
          {perRecording && (
            <>
              {renderSelect("auto_project_template_id", "template", templateOptions, templatesQuery.isPending)}
              {renderToggle("auto_project_document", "document")}
              {showRecipes && renderSelect("auto_project_recipe_id", "recipe", recipeOptions, recipesQuery.isPending)}
            </>
          )}
          {loadFailed && <p className="text-sm text-red-600">{t("workspaceSettings.autoProject.loadError")}</p>}
          {updateAutoProjectMutation.isError && (
            <p className="text-sm text-red-600">{t("workspaceSettings.saveError")}</p>
          )}
        </div>
      )}

      {/* Transcription workflow (platform admins only; the server enforces it too) */}
      {isAdmin && <WorkspaceWorkflowCard workspaceId={activeWorkspace.id} />}
    </div>
  );
}
